import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { FormFieldConfig } from '../../../models/permit-type.model';

export interface PermitFormFieldDialogData {
    field: FormFieldConfig | null;
    existingIds: string[];
}

@Component({
    selector: 'app-permit-form-field-dialog',
    templateUrl: './permit-form-field-dialog.component.html',
    styleUrls: ['./permit-form-field-dialog.component.scss'],
})
export class PermitFormFieldDialogComponent {
    readonly fieldTypes: FormFieldConfig['fieldType'][] = ['text', 'textarea', 'number', 'date', 'dropdown', 'checkbox', 'file', 'address', 'url'];

    readonly isEdit: boolean;
    form: FormGroup;
    options: string[] = [];
    newOption = '';
    optionsError = false;
    presetValueError = false;

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<PermitFormFieldDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PermitFormFieldDialogData,
    ) {
        const { field } = data;
        this.isEdit = !!field;
        this.form = this.fb.group({
            labelFr: [field?.label?.fr || '', Validators.required],
            labelEn: [field?.label?.en || '', Validators.required],
            type: [field ? field.fieldType : 'text', Validators.required],
            required: [field ? !!field.required : false],
            min: [field && field.min != null ? field.min : null],
            max: [field && field.max != null ? field.max : null],
            section: [field?.section || ''],
            presetValue: [field?.presetValue || ''],
        });
        this.options = field?.options ? field.options.map((option: any) => (typeof option === 'string' ? option : option.value)) : [];
    }

    get selectedType(): string {
        return this.form.value.type;
    }

    addOption(): void {
        const value = (this.newOption || '').trim();
        if (!value) return;
        this.options.push(value);
        this.newOption = '';
        this.optionsError = false;
    }

    removeOption(index: number): void {
        this.options.splice(index, 1);
    }

    save(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) return;
        const v = this.form.value;
        if (v.type === 'dropdown' && this.options.length === 0) {
            this.optionsError = true;
            return;
        }
        if (v.type === 'url' && !(v.presetValue || '').trim()) {
            this.presetValueError = true;
            return;
        }
        const field: FormFieldConfig = {
            fieldId: this.isEdit ? this.data.field.fieldId : this.generateFieldId(v.labelFr),
            label: { fr: v.labelFr.trim(), en: v.labelEn.trim() },
            fieldType: v.type,
            required: !!v.required,
            displayOrder: this.isEdit ? this.data.field.displayOrder : 0,
        };
        if (v.type === 'dropdown') {
            field.options = this.options.slice();
        }
        if (v.type === 'number' || v.type === 'date') {
            if (v.min != null && v.min !== '') field.min = v.type === 'number' ? Math.max(0, Number(v.min)) : Number(v.min);
            if (v.max != null && v.max !== '') field.max = v.type === 'number' ? Math.max(0, Number(v.max)) : Number(v.max);
        }
        if (v.section && v.section.trim()) {
            field.section = v.section.trim();
        }
        if (v.type === 'url') {
            field.presetValue = v.presetValue.trim();
        }
        this.dialogRef.close(field);
    }

    cancel(): void {
        this.dialogRef.close();
    }

    private generateFieldId(labelFr: string): string {
        const base =
            (labelFr || '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '_')
                .replace(/^_+|_+$/g, '') || 'champ';
        let id = base;
        let suffix = 2;
        while (this.data.existingIds.indexOf(id) !== -1) {
            id = `${base}_${suffix}`;
            suffix++;
        }
        return id;
    }
}
